// ============================================================================
// GeneraPos.jsx v2 (27/04/2026)
// Modal per generare il POS di un cantiere.
//
// NOVITA' rispetto a v1:
//  - Campi descrizione opera (breve + estesa)
//  - Organigramma cantiere esteso (Capocantiere, RL)
//  - Sezione PSC (presente, CSP, CSE, date)
//  - Selezione documenti da allegare (azienda + per ogni lavoratore)
//  - Body invio: cantiereId + descrizione + figure + psc + documentiAllegati
//
// Props:
//   cantiere: oggetto cantiere (deve avere almeno: id, nome, indirizzo, cliente)
//   onClose: () => void - chiude il modal
// ============================================================================

import React, { useState, useEffect } from 'react';
import {collection, getDocs, doc, getDoc} from "./firebase-shim";
import { db } from "./firebase";

const NAS_URL = "";

const COLORS = {
  bg: '#0f1117',
  surface: '#1a1d27',
  border: '#2a2e45',
  accent: '#f5a623',
  accentSoft: '#f5a62315',
  text: '#e8eaf2',
  textMuted: '#7b82a0',
  textDim: '#4a5068',
  success: '#3ecf8e',
  danger: '#f06060',
};

// Cache schede per sessione
let _schedeCache = null;

async function caricaSchede() {
  if (_schedeCache) return _schedeCache;
  const token = localStorage.getItem('bertoc_jwt');
  const r = await fetch(`${NAS_URL}/api/schede-sicurezza/lista`, {
    headers: { 'Authorization': 'Bearer ' + token }
  });
  if (!r.ok) throw new Error('HTTP ' + r.status);
  const data = await r.json();
  _schedeCache = data;
  return data;
}

async function caricaDocumentiDisponibili() {
  const token = localStorage.getItem('bertoc_jwt');
  const r = await fetch(`${NAS_URL}/api/pos/documenti-disponibili`, {
    headers: { 'Authorization': 'Bearer ' + token }
  });
  if (!r.ok) throw new Error('HTTP ' + r.status);
  return await r.json();
}

export default function GeneraPos({ cantiere, onClose }) {

  // ── State dati cantiere (precompilato)
  const [committente, setCommittente] = useState(cantiere?.cliente || '');
  const [indirizzo, setIndirizzo] = useState(
    [cantiere?.indirizzo, cantiere?.citta].filter(Boolean).join(', ') || ''
  );
  const [dataInizio, setDataInizio] = useState(cantiere?.dataInizio || cantiere?.inizio || '');
  const [dataFine, setDataFine] = useState(cantiere?.dataFine || cantiere?.fine || '');

  // ── State descrizione opera (NEW)
  const [descrizioneBreve, setDescrizioneBreve] = useState('');
  const [descrizioneEstesa, setDescrizioneEstesa] = useState('');

  // ── State organigramma cantiere (campi figure, NEW: capocantiere + rl)
  const [cse, setCse] = useState('');
  const [dl, setDl] = useState('');
  const [progettista, setProgettista] = useState('');
  const [capocantiere, setCapocantiere] = useState('');
  const [rl, setRl] = useState('');

  // ── State PSC (NEW)
  const [pscPresente, setPscPresente] = useState(false);
  const [csp, setCsp] = useState('');
  const [pscDataRedazione, setPscDataRedazione] = useState('');
  const [pscDataRicezione, setPscDataRicezione] = useState('');

  // F9: state per analisi PSC con AI
  const [pscAiLoading, setPscAiLoading] = useState(false);
  const [pscAiPreview, setPscAiPreview] = useState(null);
  const [pscAiError, setPscAiError] = useState(null);
  const [pscAiFilename, setPscAiFilename] = useState(null);

  // F8.1: note specifiche del cantiere
  const [noteCantiere, setNoteCantiere] = useState('');

  // ── Lavoratori
  const [lavoratori, setLavoratori] = useState([]);
  const [lavoratoriSelezionati, setLavoratoriSelezionati] = useState(new Set());
  const [loadingLav, setLoadingLav] = useState(true);

  // ── Schede sicurezza
  const [tutteSchede, setTutteSchede] = useState([]);
  const [schedeSelezionate, setSchedeSelezionate] = useState(new Set());
  const [filtroSchede, setFiltroSchede] = useState('');
  const [filtroMacro, setFiltroMacro] = useState('');
  const [macroCategorie, setMacroCategorie] = useState({});
  const [autoSchede, setAutoSchede] = useState(true);

  // ── Documenti da allegare (NEW)
  const [documentiDisponibili, setDocumentiDisponibili] = useState({ azienda: [], lavoratori: {} });
  const [documentiSelezionati, setDocumentiSelezionati] = useState(new Set());
  const [loadingDoc, setLoadingDoc] = useState(true);
  const [lavoratoriEspansi, setLavoratoriEspansi] = useState(new Set());

  // ── Macchine in cantiere (Step B)
  const [macchineDisponibili, setMacchineDisponibili] = useState([]);
  const [macchineCantiere, setMacchineCantiere] = useState(new Set());
  const [loadingMacchine, setLoadingMacchine] = useState(true);

  // ── Modalita organizzative (Step M4)
  const MOD_VUOTO = {
    aspetti: {
      recinzione:           { descrizione: "", vedasiAllegato: false },
      viabilita:            { descrizione: "", vedasiAllegato: false },
      depositoMateriali:    { descrizione: "", vedasiAllegato: false },
      depositoAttrezzature: { descrizione: "", vedasiAllegato: false },
      altro:                { descrizione: "", vedasiAllegato: false },
    },
    servizi: {
      prontoSoccorso: { opzione: "", chi: "", note: "" },
      wc:             { opzione: "", chi: "", note: "" },
      lavabi:         { opzione: "", chi: "", note: "" },
      docce:          { opzione: "", chi: "", note: "" },
      spogliatoi:     { opzione: "", chi: "", note: "" },
      altro:          { opzione: "", chi: "", note: "" },
    },
  };
  const [modOrg, setModOrg] = useState(MOD_VUOTO);
  const [modOrgEspansa, setModOrgEspansa] = useState(false);

  // ── Rifiuti (Step R3)
  const RIF_VUOTO = {
    responsabile: "",
    tipologia: { pericolosi: "", nonPericolosi: "", altro: "" },
    documentazione: {
      formulario:      { stato: "", note: "" },
      registroCarScar: { stato: "", note: "" },
      altro: "",
    },
    misure: {
      refettorio:  { stato: "", note: "" },
      spogliatoio: { stato: "", note: "" },
      baracche:    { stato: "", note: "" },
      ufficio:     { stato: "", note: "" },
      wcChimico:   { stato: "", note: "" },
    },
  };
  const [rifiutiCantiere, setRifiutiCantiere] = useState(RIF_VUOTO);
  const [rifEspansa, setRifEspansa] = useState(false);

  const [generando, setGenerando] = useState(false);
  const [errore, setErrore] = useState(null);

  // ── Carica lavoratori, schede, dizionario, documenti al mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Lavoratori
        const snap = await getDocs(collection(db, "lavoratori_bertoc"));
        if (cancelled) return;
        const arr = [];
        snap.forEach(doc => {
          const d = doc.data();
          if (d.attivo !== false) {
            arr.push({ id: doc.id, ...d });
          }
        });
        arr.sort((a, b) => (a.cognome || '').localeCompare(b.cognome || ''));
        setLavoratori(arr);
        setLavoratoriSelezionati(new Set(arr.map(l => l.id)));
        setLoadingLav(false);

        // Schede sicurezza
        try {
          const sched = await caricaSchede();
          if (cancelled) return;
          setTutteSchede(sched.schede || []);
          setMacroCategorie(sched.macro_categorie || {});
        } catch (e) {
          console.error('Errore carica schede:', e);
        }

        // Auto-selezione schede da dizionario
        if (autoSchede && cantiere?.lavorazioni?.length) {
          try {
            const dizSnap = await getDoc(doc(db, "impostazioni", "dizionario"));
            if (cancelled) return;
            if (dizSnap.exists()) {
              const voci = dizSnap.data().voci || {};
              const codiciAuto = new Set();
              for (const lav of (cantiere.lavorazioni || [])) {
                const cat = (lav.categoria || '').toLowerCase().trim();
                const sub = (lav.sottocategoria || '').toLowerCase().trim();
                if (!cat || !voci[cat]) continue;
                const subDati = voci[cat]?.sottocategorie?.[sub];
                if (subDati?.schedeSicurezza) {
                  for (const cod of subDati.schedeSicurezza) {
                    codiciAuto.add(cod);
                  }
                }
              }
              if (codiciAuto.size > 0) {
                setSchedeSelezionate(codiciAuto);
              }
            }
          } catch (e) {
            console.warn('Errore auto schede:', e);
          }
        }

        // Documenti disponibili (NEW)
        try {
          const docs = await caricaDocumentiDisponibili();
          if (cancelled) return;
          setDocumentiDisponibili(docs);
        } catch (e) {
          console.warn('Errore carica documenti:', e);
        } finally {
          if (!cancelled) setLoadingDoc(false);
        }

        // Macchine attrezzature (Step B)
        try {
          const snapMacchine = await getDoc(doc(db, "impostazioni", "macchine_attrezzature"));
          if (cancelled) return;
          if (snapMacchine.exists()) {
            const data = snapMacchine.data();
            const lista = Array.isArray(data.voci) ? data.voci : [];
            setMacchineDisponibili(lista.slice().sort((a, b) => (a.ordine || 0) - (b.ordine || 0)));
          } else {
            setMacchineDisponibili([]);
          }
        } catch (e) {
          console.warn('Errore carica macchine:', e);
        } finally {
          if (!cancelled) setLoadingMacchine(false);
        }

        // Modalita organizzative default (Step M4)
        try {
          const snapMod = await getDoc(doc(db, "impostazioni", "modalita_organizzative_default"));
          if (cancelled) return;
          if (snapMod.exists()) {
            const data = snapMod.data();
            const merged = {
              aspetti: { ...MOD_VUOTO.aspetti, ...(data.aspetti || {}) },
              servizi: { ...MOD_VUOTO.servizi, ...(data.servizi || {}) },
            };
            setModOrg(merged);
          }
        } catch (e) {
          console.warn('Errore carica modalita default:', e);
        }

        // Rifiuti default (Step R3)
        try {
          const snapRif = await getDoc(doc(db, "impostazioni", "rifiuti_default"));
          if (cancelled) return;
          if (snapRif.exists()) {
            const data = snapRif.data();
            const merged = {
              responsabile: data.responsabile || "",
              tipologia: { ...RIF_VUOTO.tipologia, ...(data.tipologia || {}) },
              documentazione: { ...RIF_VUOTO.documentazione, ...(data.documentazione || {}) },
              misure: { ...RIF_VUOTO.misure, ...(data.misure || {}) },
            };
            setRifiutiCantiere(merged);
          }
        } catch (e) {
          console.warn('Errore carica rifiuti default:', e);
        }
      } catch (e) {
        if (!cancelled) {
          setErrore('Errore caricamento dati: ' + e.message);
          setLoadingLav(false);
          setLoadingDoc(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [cantiere, autoSchede]);

  // ── Toggle helpers
  function toggleLavoratore(id) {
    const next = new Set(lavoratoriSelezionati);
    if (next.has(id)) next.delete(id); else next.add(id);
    setLavoratoriSelezionati(next);
  }

  function toggleScheda(cod) {
    const next = new Set(schedeSelezionate);
    if (next.has(cod)) next.delete(cod); else next.add(cod);
    setSchedeSelezionate(next);
  }

  function toggleDocumento(path) {
    const next = new Set(documentiSelezionati);
    if (next.has(path)) next.delete(path); else next.add(path);
    setDocumentiSelezionati(next);
  }

  function toggleLavoratoreEspanso(nome) {
    const next = new Set(lavoratoriEspansi);
    if (next.has(nome)) next.delete(nome); else next.add(nome);
    setLavoratoriEspansi(next);
  }

  function toggleMacchina(id) {
    const next = new Set(macchineCantiere);
    if (next.has(id)) next.delete(id); else next.add(id);
    setMacchineCantiere(next);
  }

  function aggiornaModAspetto(key, campo, valore) {
    setModOrg(prev => ({
      ...prev,
      aspetti: { ...prev.aspetti, [key]: { ...prev.aspetti[key], [campo]: valore } }
    }));
  }

  function aggiornaModServizio(key, campo, valore) {
    setModOrg(prev => ({
      ...prev,
      servizi: { ...prev.servizi, [key]: { ...prev.servizi[key], [campo]: valore } }
    }));
  }

  function aggiornaRifTipologia(campo, valore) {
    setRifiutiCantiere(prev => ({ ...prev, tipologia: { ...prev.tipologia, [campo]: valore } }));
  }
  function aggiornaRifDocumentazione(key, campo, valore) {
    setRifiutiCantiere(prev => {
      if (key === "altro") return { ...prev, documentazione: { ...prev.documentazione, altro: valore } };
      return { ...prev, documentazione: { ...prev.documentazione, [key]: { ...prev.documentazione[key], [campo]: valore } } };
    });
  }
  function aggiornaRifMisure(key, campo, valore) {
    setRifiutiCantiere(prev => ({ ...prev, misure: { ...prev.misure, [key]: { ...prev.misure[key], [campo]: valore } } }));
  }

  // Helper: appiattisce ricorsivamente i file (incluse sottocartelle)
  // F9: carica PSC e fa analizzare ad AI
  async function caricaPSCAi(file) {
    if (!file) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setPscAiError('Solo file PDF accettati');
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      setPscAiError('PDF troppo grande (max 25MB)');
      return;
    }
    if (!cantiere?.id && !cantiere?._id) {
      setPscAiError('Cantiere non identificato. Salva il cantiere prima di analizzare il PSC.');
      return;
    }
    setPscAiError(null);
    setPscAiLoading(true);
    setPscAiPreview(null);
    try {
      // Leggo il file come base64
      const reader = new FileReader();
      const pdfBase64 = await new Promise((resolve, reject) => {
        reader.onload = () => {
          const result = reader.result;
          // result e' "data:application/pdf;base64,XXXXX..."
          const idx = result.indexOf(',');
          resolve(idx > 0 ? result.slice(idx + 1) : result);
        };
        reader.onerror = () => reject(new Error('Errore lettura file'));
        reader.readAsDataURL(file);
      });

      const token = localStorage.getItem('bertoc_jwt');
      const r = await fetch(`${NAS_URL}/api/pos/analizza-psc`, {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          cantiereId: cantiere?.id || cantiere?._id,
          pdfBase64,
        }),
      });
      const j = await r.json();
      if (!r.ok || !j.ok) {
        throw new Error(j.error || ('HTTP ' + r.status));
      }
      setPscAiPreview(j.dati || {});
      setPscAiFilename(j.pscFilename || null);
    } catch (err) {
      console.error('[F9] Errore analisi PSC:', err);
      setPscAiError(err.message);
    } finally {
      setPscAiLoading(false);
    }
  }

  // F9: applica i dati estratti dall'AI ai campi del POS
  function applicaPSCAi() {
    if (!pscAiPreview) return;
    const d = pscAiPreview;
    setPscPresente(true);
    if (d.cspNome) setCsp(d.cspNome);
    if (d.cseNome) setCse(d.cseNome);
    if (d.dataRedazione) setPscDataRedazione(d.dataRedazione);
    if (d.indirizzoCantiere) setIndirizzo(d.indirizzoCantiere);
    if (d.committente) setCommittente(d.committente);
    if (d.descrizioneOpera) setDescrizioneEstesa(d.descrizioneOpera);
    if (d.responsabileLavori && d.responsabileLavori !== 'Non nominato') setRl(d.responsabileLavori);
    if (d.direttoreLavori) setDl(d.direttoreLavori);
    if (d.progettista) setProgettista(d.progettista);
    if (d.noteAggiuntive) setNoteCantiere(d.noteAggiuntive);
    setPscAiPreview(null);
  }

  function appiattisciDocumenti(nodes) {
    const out = [];
    for (const n of nodes) {
      if (n.isDir && Array.isArray(n.children)) {
        out.push(...appiattisciDocumenti(n.children));
      } else if (!n.isDir) {
        out.push(n);
      }
    }
    return out;
  }

  // Schede filtrate
  let schedeFiltrate = tutteSchede;
  if (filtroMacro) schedeFiltrate = schedeFiltrate.filter(s => s.macro_codice === filtroMacro);
  if (filtroSchede) {
    const q = filtroSchede.toLowerCase();
    schedeFiltrate = schedeFiltrate.filter(s =>
      s.codice.toLowerCase().includes(q) ||
      (s.nome || '').toLowerCase().includes(q)
    );
  }
  const perMacro = {};
  for (const s of schedeFiltrate) {
    if (!perMacro[s.macro_codice]) perMacro[s.macro_codice] = [];
    perMacro[s.macro_codice].push(s);
  }

  async function generaPOS() {
    setGenerando(true);
    setErrore(null);
    try {
      const lavSel = lavoratori.filter(l => lavoratoriSelezionati.has(l.id));
      const codiciSel = Array.from(schedeSelezionate);
      const documentiSel = Array.from(documentiSelezionati);
      const macchineSel = Array.from(macchineCantiere);

      const body = {
        // Modo nuovo: passa cantiereId per far leggere al backend i dati aggiornati
        cantiereId: cantiere?.id || cantiere?._id,
        // Backward compat: se backend non ha cantiereId valido, usa cantiere oggetto
        cantiere: {
          nome: cantiere?.nome || 'Cantiere',
          committente,
          indirizzo,
          dataInizio,
          dataFine,
          cse,
          dl,
          progettista,
        },
        descrizioneBreve,
        descrizioneEstesa,
        figure: {
          dl,
          progettista,
          capocantiere,
          rl,
        },
        psc: {
          presente: pscPresente,
          csp,
          cse,
          dataRedazione: pscDataRedazione,
          dataRicezione: pscDataRicezione,
        },
        documentiAllegati: documentiSel,
        macchineCantiere: macchineSel,
        modalitaOrganizzative: modOrg,
        noteCantiere,
        rifiuti: rifiutiCantiere,
        schedeCodici: codiciSel,
        lavoratori: lavSel,
      };

      const token = localStorage.getItem('bertoc_jwt');
      const r = await fetch(`${NAS_URL}/api/pos/genera`, {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!r.ok) {
        const err = await r.text();
        throw new Error('HTTP ' + r.status + ': ' + err);
      }

      const blob = await r.blob();
      const u = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = u;
      a.download = 'POS_' + (cantiere?.nome || 'cantiere').replace(/[^a-zA-Z0-9]/g, '_') + '.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(u);

      console.log('POS generato:', blob.size, 'byte');
    } catch (e) {
      setErrore('Errore generazione: ' + e.message);
    } finally {
      setGenerando(false);
    }
  }

  // Stile generale input
  const inputSt = {
    background: COLORS.bg,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 6,
    color: COLORS.text,
    padding: '6px 10px',
    fontSize: 12,
    fontFamily: 'inherit',
    outline: 'none',
  };

  const labelSt = { fontSize: 10, color: COLORS.textDim, display: 'block', marginBottom: 2 };
  const sezioneTitSt = { fontSize: 12, color: COLORS.accent, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 };

  // Conta totale documenti azienda + lavoratori
  const totDocAzienda = appiattisciDocumenti(documentiDisponibili.azienda || []).length;
  const totDocLav = Object.values(documentiDisponibili.lavoratori || {})
    .reduce((sum, files) => sum + appiattisciDocumenti(files).length, 0);

  return (
    <div onClick={onClose} style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 9999, padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12,
        padding: 24, maxWidth: 900, width: '100%', maxHeight: '92vh',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, borderBottom: `1px solid ${COLORS.border}`, paddingBottom: 12 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.text }}>
              📋 Genera POS — {cantiere?.nome || 'Cantiere'}
            </div>
            <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 2 }}>
              Piano Operativo di Sicurezza (D.Lgs. 81/2008 art. 89)
            </div>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: COLORS.textMuted,
            cursor: 'pointer', fontSize: 22,
          }}>✕</button>
        </div>

        {errore && (
          <div style={{
            background: '#2a0f12', border: '1px solid #f0606044', borderRadius: 6,
            padding: '8px 12px', marginBottom: 12, fontSize: 12, color: '#f06060',
          }}>⚠️ {errore}</div>
        )}

        <div style={{ overflowY: 'auto', flex: 1, paddingRight: 8 }}>

          {/* === DESCRIZIONE OPERA (NEW) === */}
          <div style={{ marginBottom: 20 }}>
            <div style={sezioneTitSt}>📝 Descrizione opera</div>
            <div style={{ display: 'grid', gap: 8 }}>
              <div>
                <label style={labelSt}>Descrizione breve (1 riga, finirà sul frontespizio)</label>
                <input
                  style={{ ...inputSt, width: '100%' }}
                  placeholder="es. Ristrutturazione completa appartamento"
                  value={descrizioneBreve}
                  onChange={e => setDescrizioneBreve(e.target.value)}
                />
              </div>
              <div>
                <label style={labelSt}>Descrizione estesa (paragrafo per sezione dedicata)</label>
                <textarea
                  style={{ ...inputSt, width: '100%', minHeight: 70, resize: 'vertical', fontFamily: 'inherit' }}
                  placeholder="Dettaglio dei lavori da svolgere, descrizione narrativa..."
                  value={descrizioneEstesa}
                  onChange={e => setDescrizioneEstesa(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* === DATI CANTIERE === */}
          <div style={{ marginBottom: 20 }}>
            <div style={sezioneTitSt}>Dati cantiere</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <label style={labelSt}>Committente</label>
                <input style={{ ...inputSt, width: '100%' }} value={committente} onChange={e => setCommittente(e.target.value)} />
              </div>
              <div>
                <label style={labelSt}>Indirizzo cantiere</label>
                <input style={{ ...inputSt, width: '100%' }} value={indirizzo} onChange={e => setIndirizzo(e.target.value)} />
              </div>
              <div>
                <label style={labelSt}>Data inizio</label>
                <input style={{ ...inputSt, width: '100%' }} placeholder="gg/mm/aaaa" value={dataInizio} onChange={e => setDataInizio(e.target.value)} />
              </div>
              <div>
                <label style={labelSt}>Data fine prevista</label>
                <input style={{ ...inputSt, width: '100%' }} placeholder="gg/mm/aaaa" value={dataFine} onChange={e => setDataFine(e.target.value)} />
              </div>
            </div>
          </div>

          {/* === ORGANIGRAMMA CANTIERE === */}
          <div style={{ marginBottom: 20 }}>
            <div style={sezioneTitSt}>👥 Organigramma cantiere</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <label style={labelSt}>Direttore Lavori</label>
                <input style={{ ...inputSt, width: '100%' }} placeholder="Nome" value={dl} onChange={e => setDl(e.target.value)} />
              </div>
              <div>
                <label style={labelSt}>Progettista</label>
                <input style={{ ...inputSt, width: '100%' }} placeholder="Nome" value={progettista} onChange={e => setProgettista(e.target.value)} />
              </div>
              <div>
                <label style={labelSt}>Capocantiere</label>
                <input style={{ ...inputSt, width: '100%' }} placeholder="Nome" value={capocantiere} onChange={e => setCapocantiere(e.target.value)} />
              </div>
              <div>
                <label style={labelSt}>Responsabile Lavori (RL)</label>
                <input style={{ ...inputSt, width: '100%' }} placeholder="Nome" value={rl} onChange={e => setRl(e.target.value)} />
              </div>
            </div>
          </div>

          {/* === PSC (NEW) === */}
          <div style={{ marginBottom: 20 }}>
            <div style={sezioneTitSt}>🛡️ Coordinamento sicurezza (PSC)</div>
            <div onClick={() => setPscPresente(!pscPresente)} style={{
              display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
              padding: '6px 10px', borderRadius: 6, marginBottom: 8,
              background: pscPresente ? COLORS.accentSoft : COLORS.bg,
              border: `1px solid ${pscPresente ? COLORS.accent + '88' : COLORS.border}`,
            }}>
              <div style={{
                width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                border: `2px solid ${pscPresente ? COLORS.accent : COLORS.border}`,
                background: pscPresente ? COLORS.accent : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, color: '#1a1d27', fontWeight: 700,
              }}>{pscPresente ? '✓' : ''}</div>
              <div style={{ fontSize: 12, color: COLORS.text }}>
                Cantiere con PSC (presenza coordinatore sicurezza)
              </div>
            </div>
            {pscPresente && (
              <>
                {/* F9: bottone Carica PSC con AI */}
                <div style={{ marginBottom: 10, padding: 10, background: COLORS.bg, border: `1px dashed ${COLORS.border}`, borderRadius: 6 }}>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 6 }}>
                    Hai il PDF del PSC? Caricalo e l'AI compilera' automaticamente i campi qui sotto + indirizzo, committente, descrizione opera, note.
                  </div>
                  <label style={{
                    display: 'inline-block', padding: '6px 12px', background: COLORS.accent, color: '#1a1d27',
                    borderRadius: 4, cursor: pscAiLoading ? 'wait' : 'pointer', fontSize: 12, fontWeight: 700,
                    opacity: pscAiLoading ? 0.6 : 1,
                  }}>
                    {pscAiLoading ? '⏳ AI sta leggendo... (30-60s)' : '📎 Carica PSC e analizza con AI'}
                    <input type="file" accept="application/pdf" disabled={pscAiLoading}
                      onChange={e => { const f = e.target.files && e.target.files[0]; if (f) caricaPSCAi(f); e.target.value = ''; }}
                      style={{ display: 'none' }} />
                  </label>
                  {pscAiError && (
                    <div style={{ marginTop: 6, padding: '6px 8px', background: '#3a1f1f', color: '#ff8888', fontSize: 11, borderRadius: 4 }}>
                      ⚠️ {pscAiError}
                    </div>
                  )}
                  {pscAiPreview && (
                    <div style={{ marginTop: 8, padding: 10, background: COLORS.accentSoft, border: `1px solid ${COLORS.accent}`, borderRadius: 4 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.accent, marginBottom: 6 }}>
                        ✓ AI ha estratto questi dati dal PSC{pscAiFilename ? ' (' + pscAiFilename + ')' : ''}:
                      </div>
                      <div style={{ fontSize: 11, color: COLORS.text, lineHeight: 1.6, marginBottom: 8 }}>
                        {pscAiPreview.cspNome && <div><strong>CSP:</strong> {pscAiPreview.cspNome}</div>}
                        {pscAiPreview.cseNome && <div><strong>CSE:</strong> {pscAiPreview.cseNome}</div>}
                        {pscAiPreview.dataRedazione && <div><strong>Data redazione:</strong> {pscAiPreview.dataRedazione}</div>}
                        {pscAiPreview.indirizzoCantiere && <div><strong>Indirizzo:</strong> {pscAiPreview.indirizzoCantiere}</div>}
                        {pscAiPreview.committente && <div><strong>Committente:</strong> {pscAiPreview.committente}</div>}
                        {pscAiPreview.importoLavori && <div><strong>Importo:</strong> {pscAiPreview.importoLavori} €</div>}
                        {pscAiPreview.responsabileLavori && pscAiPreview.responsabileLavori !== 'Non nominato' && <div><strong>Resp. lavori:</strong> {pscAiPreview.responsabileLavori}</div>}
                        {pscAiPreview.direttoreLavori && <div><strong>Direttore lavori:</strong> {pscAiPreview.direttoreLavori}</div>}
                        {pscAiPreview.progettista && <div><strong>Progettista:</strong> {pscAiPreview.progettista}</div>}
                        {pscAiPreview.descrizioneOpera && <div style={{ marginTop: 4 }}><strong>Descrizione opera:</strong><br/><span style={{ fontStyle: 'italic' }}>{pscAiPreview.descrizioneOpera.substring(0, 250)}{pscAiPreview.descrizioneOpera.length > 250 ? '...' : ''}</span></div>}
                        {pscAiPreview.noteAggiuntive && <div style={{ marginTop: 4 }}><strong>Note:</strong><br/><span style={{ fontStyle: 'italic' }}>{pscAiPreview.noteAggiuntive.substring(0, 250)}{pscAiPreview.noteAggiuntive.length > 250 ? '...' : ''}</span></div>}
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={applicaPSCAi} style={{
                          padding: '6px 14px', background: COLORS.accent, color: '#1a1d27',
                          border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 700,
                        }}>✓ Applica al POS</button>
                        <button onClick={() => setPscAiPreview(null)} style={{
                          padding: '6px 14px', background: COLORS.bg, color: COLORS.text,
                          border: `1px solid ${COLORS.border}`, borderRadius: 4, cursor: 'pointer', fontSize: 12,
                        }}>✗ Scarta</button>
                      </div>
                    </div>
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div>
                  <label style={labelSt}>CSP — Coord. Sicurezza Progettazione</label>
                  <input style={{ ...inputSt, width: '100%' }} placeholder="Nome" value={csp} onChange={e => setCsp(e.target.value)} />
                </div>
                <div>
                  <label style={labelSt}>CSE — Coord. Sicurezza Esecuzione</label>
                  <input style={{ ...inputSt, width: '100%' }} placeholder="Nome" value={cse} onChange={e => setCse(e.target.value)} />
                </div>
                <div>
                  <label style={labelSt}>Data redazione PSC</label>
                  <input style={{ ...inputSt, width: '100%' }} placeholder="gg/mm/aaaa" value={pscDataRedazione} onChange={e => setPscDataRedazione(e.target.value)} />
                </div>
                <div>
                  <label style={labelSt}>Data ricezione PSC</label>
                  <input style={{ ...inputSt, width: '100%' }} placeholder="gg/mm/aaaa" value={pscDataRicezione} onChange={e => setPscDataRicezione(e.target.value)} />
                </div>
                </div>
              </>
            )}
          </div>

          {/* === LAVORATORI === */}
          <div style={{ marginBottom: 20 }}>
            <div style={sezioneTitSt}>
              Lavoratori in cantiere ({lavoratoriSelezionati.size}/{lavoratori.length})
            </div>
            {loadingLav ? (
              <div style={{ fontSize: 12, color: COLORS.textMuted, padding: 10 }}>Caricamento...</div>
            ) : lavoratori.length === 0 ? (
              <div style={{ fontSize: 12, color: COLORS.textMuted, padding: 10, fontStyle: 'italic' }}>
                Nessun lavoratore registrato.
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 6 }}>
                {lavoratori.map(l => {
                  const sel = lavoratoriSelezionati.has(l.id);
                  return (
                    <div key={l.id} onClick={() => toggleLavoratore(l.id)} style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                      borderRadius: 6, cursor: 'pointer',
                      background: sel ? COLORS.accentSoft : COLORS.bg,
                      border: `1px solid ${sel ? COLORS.accent + '88' : COLORS.border}`,
                    }}>
                      <div style={{
                        width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                        border: `2px solid ${sel ? COLORS.accent : COLORS.border}`,
                        background: sel ? COLORS.accent : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 10, color: '#1a1d27', fontWeight: 700,
                      }}>{sel ? '✓' : ''}</div>
                      <div style={{ fontSize: 12, color: COLORS.text }}>
                        {l.nome_completo || `${l.nome || ''} ${l.cognome || ''}`.trim()}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* === SCHEDE === */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={sezioneTitSt}>
                Schede sicurezza ({schedeSelezionate.size}/{tutteSchede.length})
              </div>
              <button onClick={() => setSchedeSelezionate(new Set())} style={{
                background: 'none', border: 'none', color: COLORS.textMuted,
                fontSize: 10, cursor: 'pointer', textTransform: 'uppercase',
              }}>Deseleziona tutte</button>
            </div>

            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
              <input
                type="text"
                placeholder="Cerca codice o nome..."
                value={filtroSchede}
                onChange={e => setFiltroSchede(e.target.value)}
                style={{ ...inputSt, flex: 1 }}
              />
              <select value={filtroMacro} onChange={e => setFiltroMacro(e.target.value)} style={{ ...inputSt, minWidth: 200 }}>
                <option value="">Tutte le categorie</option>
                {Object.entries(macroCategorie).sort().map(([cod, nome]) => (
                  <option key={cod} value={cod}>{cod} — {nome}</option>
                ))}
              </select>
            </div>

            <div style={{ maxHeight: 240, overflowY: 'auto', border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: 6 }}>
              {Object.keys(perMacro).sort().map(macro => (
                <div key={macro} style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 10, color: COLORS.accent, fontWeight: 700, marginBottom: 3 }}>
                    {macro} — {macroCategorie[macro] || macro}
                  </div>
                  {perMacro[macro].map(s => {
                    const sel = schedeSelezionate.has(s.codice);
                    return (
                      <div key={s.codice} onClick={() => toggleScheda(s.codice)} style={{
                        display: 'flex', alignItems: 'center', gap: 6, padding: '3px 6px',
                        cursor: 'pointer', borderRadius: 4,
                        background: sel ? COLORS.accentSoft : 'transparent',
                      }}>
                        <div style={{
                          width: 14, height: 14, borderRadius: 3, flexShrink: 0,
                          border: `2px solid ${sel ? COLORS.accent : COLORS.border}`,
                          background: sel ? COLORS.accent : 'transparent',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 9, color: '#1a1d27', fontWeight: 700,
                        }}>{sel ? '✓' : ''}</div>
                        <div style={{ fontSize: 11, color: COLORS.accent, fontFamily: 'monospace', fontWeight: 700, minWidth: 50 }}>
                          {s.codice}
                        </div>
                        <div style={{ fontSize: 11, color: COLORS.text, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {s.nome}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          {/* === MODALITA ORGANIZZATIVE (Step M4) === */}
          <div style={{ marginBottom: 16, border: `1px solid ${COLORS.border}`, borderRadius: 6 }}>
            <div onClick={() => setModOrgEspansa(!modOrgEspansa)} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 12px', cursor: 'pointer',
              background: modOrgEspansa ? COLORS.accentSoft : 'transparent',
              borderRadius: 6,
            }}>
              <div style={sezioneTitSt}>🔧 Modalità organizzative del cantiere</div>
              <div style={{ fontSize: 12, color: COLORS.textMuted }}>{modOrgEspansa ? '▼ Comprimi' : '▶ Espandi'}</div>
            </div>

            {modOrgEspansa && (
              <div style={{ padding: '12px', borderTop: `1px solid ${COLORS.border}` }}>
                <div style={{ fontSize: 11, color: COLORS.textMuted, fontStyle: 'italic', marginBottom: 12 }}>
                  Pre-compilato con i default delle Impostazioni. Modifiche valide solo per questo cantiere.
                </div>

                {/* ASPETTI */}
                <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.accent, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
                  Aspetti generali
                </div>
                {[
                  { key: "recinzione",           label: "Recinzione" },
                  { key: "viabilita",            label: "Viabilità ed accessi" },
                  { key: "depositoMateriali",    label: "Deposito materiali" },
                  { key: "depositoAttrezzature", label: "Deposito attrezzature" },
                  { key: "altro",                label: "Altro" },
                ].map(r => {
                  const val = modOrg.aspetti[r.key] || { descrizione: "", vedasiAllegato: false };
                  return (
                    <div key={r.key} style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 3, color: COLORS.text }}>{r.label}</div>
                      <textarea style={{
                        width: '100%', minHeight: 36, padding: '6px 8px',
                        background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 4,
                        color: COLORS.text, fontSize: 12, fontFamily: 'inherit', resize: 'vertical',
                      }}
                        placeholder="Descrizione..."
                        value={val.descrizione}
                        onChange={e => aggiornaModAspetto(r.key, "descrizione", e.target.value)} />
                      <label style={{ fontSize: 11, color: COLORS.textMuted, display: 'flex', alignItems: 'center', gap: 4, marginTop: 3, cursor: 'pointer' }}>
                        <input type="checkbox" checked={val.vedasiAllegato}
                          onChange={e => aggiornaModAspetto(r.key, "vedasiAllegato", e.target.checked)} />
                        Vedasi documentazione allegata
                      </label>
                    </div>
                  );
                })}

                {/* SERVIZI */}
                <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.accent, textTransform: 'uppercase', letterSpacing: 1, marginTop: 14, marginBottom: 8 }}>
                  Servizi igienico-assistenziali
                </div>
                {[
                  { key: "prontoSoccorso", label: "Pronto Soccorso" },
                  { key: "wc",             label: "WC" },
                  { key: "lavabi",         label: "Lavabi" },
                  { key: "docce",          label: "Docce" },
                  { key: "spogliatoi",     label: "Spogliatoi" },
                  { key: "altro",          label: "Altro" },
                ].map(r => {
                  const val = modOrg.servizi[r.key] || { opzione: "", chi: "", note: "" };
                  return (
                    <div key={r.key} style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 3 }}>{r.label}</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, fontSize: 11, color: COLORS.text }}>
                        {[
                          { v: "committente",    l: "Committente",     chi: true },
                          { v: "non_pertinente", l: "Non pertinente",  chi: false },
                          { v: "accordo",        l: "Accordo con",     chi: true },
                          { v: "impresa",        l: "Impresa esecutrice", chi: false },
                        ].map(o => (
                          <label key={o.v} style={{ display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer' }}>
                            <input type="radio" name={`p_${r.key}`} checked={val.opzione === o.v}
                              onChange={() => aggiornaModServizio(r.key, "opzione", o.v)} />
                            <span>{o.l}</span>
                          </label>
                        ))}
                      </div>
                      {(val.opzione === "committente" || val.opzione === "accordo") && (
                        <input style={{
                          width: '100%', padding: '4px 8px', marginTop: 4,
                          background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 4,
                          color: COLORS.text, fontSize: 11,
                        }}
                          placeholder="Chi?"
                          value={val.chi}
                          onChange={e => aggiornaModServizio(r.key, "chi", e.target.value)} />
                      )}
                      <input style={{
                        width: '100%', padding: '4px 8px', marginTop: 4,
                        background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 4,
                        color: COLORS.text, fontSize: 11,
                      }}
                        placeholder="Note (opz.)"
                        value={val.note}
                        onChange={e => aggiornaModServizio(r.key, "note", e.target.value)} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* === GESTIONE RIFIUTI (Step R3) === */}
          <div style={{ marginBottom: 16, border: `1px solid ${COLORS.border}`, borderRadius: 6 }}>
            <div onClick={() => setRifEspansa(!rifEspansa)} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 12px', cursor: 'pointer',
              background: rifEspansa ? COLORS.accentSoft : 'transparent',
              borderRadius: 6,
            }}>
              <div style={sezioneTitSt}>🗑️ Gestione rifiuti del cantiere</div>
              <div style={{ fontSize: 12, color: COLORS.textMuted }}>{rifEspansa ? '▼ Comprimi' : '▶ Espandi'}</div>
            </div>

            {rifEspansa && (
              <div style={{ padding: '12px', borderTop: `1px solid ${COLORS.border}` }}>
                <div style={{ fontSize: 11, color: COLORS.textMuted, fontStyle: 'italic', marginBottom: 12 }}>
                  Pre-compilato dai default. Modifiche valide solo per questo cantiere.
                </div>

                {/* Responsabile */}
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 3 }}>Responsabile gestione rifiuti</div>
                  <input style={{ width: '100%', padding: '6px 8px', background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 4, color: COLORS.text, fontSize: 12 }}
                    placeholder="Nome responsabile"
                    value={rifiutiCantiere.responsabile}
                    onChange={e => setRifiutiCantiere(prev => ({ ...prev, responsabile: e.target.value }))} />
                </div>

                {/* Tipologia */}
                <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.accent, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Tipologia rifiuti</div>
                {[
                  { key: "pericolosi",    label: "Pericolosi" },
                  { key: "nonPericolosi", label: "Non pericolosi" },
                ].map(r => {
                  const v = rifiutiCantiere.tipologia[r.key] || "";
                  return (
                    <div key={r.key} style={{ marginBottom: 8, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <div style={{ fontSize: 11, fontWeight: 600, minWidth: 110 }}>{r.label}</div>
                      {[{v:"si",l:"SI"},{v:"no",l:"NO"},{v:"da_definire",l:"Da definire"}].map(o => (
                        <label key={o.v} style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 11, cursor: "pointer" }}>
                          <input type="radio" name={`rt_${r.key}`} checked={v === o.v}
                            onChange={() => aggiornaRifTipologia(r.key, o.v)} />
                          <span>{o.l}</span>
                        </label>
                      ))}
                    </div>
                  );
                })}
                <input style={{ width: '100%', padding: '4px 8px', marginTop: 6, marginBottom: 12, background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 4, color: COLORS.text, fontSize: 11 }}
                  placeholder="Altro tipo rifiuto (specificare)"
                  value={rifiutiCantiere.tipologia.altro}
                  onChange={e => aggiornaRifTipologia("altro", e.target.value)} />

                {/* Documentazione */}
                <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.accent, textTransform: 'uppercase', letterSpacing: 1, marginTop: 14, marginBottom: 8 }}>Documentazione</div>
                {[
                  { key: "formulario",      label: "Formulario" },
                  { key: "registroCarScar", label: "Registro carico/scarico" },
                ].map(r => {
                  const v = rifiutiCantiere.documentazione[r.key] || { stato: "", note: "" };
                  return (
                    <div key={r.key} style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 3 }}>{r.label}</div>
                      <div style={{ display: "flex", gap: 10, marginBottom: 4 }}>
                        {[{v:"si",l:"SI"},{v:"no",l:"NO"}].map(o => (
                          <label key={o.v} style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 11, cursor: "pointer" }}>
                            <input type="radio" name={`rd_${r.key}`} checked={v.stato === o.v}
                              onChange={() => aggiornaRifDocumentazione(r.key, "stato", o.v)} />
                            <span>{o.l}</span>
                          </label>
                        ))}
                      </div>
                      <input style={{ width: '100%', padding: '4px 8px', background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 4, color: COLORS.text, fontSize: 11 }}
                        placeholder="Note"
                        value={v.note}
                        onChange={e => aggiornaRifDocumentazione(r.key, "note", e.target.value)} />
                    </div>
                  );
                })}
                <input style={{ width: '100%', padding: '4px 8px', marginTop: 4, marginBottom: 12, background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 4, color: COLORS.text, fontSize: 11 }}
                  placeholder="Altro documento"
                  value={rifiutiCantiere.documentazione.altro || ""}
                  onChange={e => aggiornaRifDocumentazione("altro", null, e.target.value)} />

                {/* Misure */}
                <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.accent, textTransform: 'uppercase', letterSpacing: 1, marginTop: 14, marginBottom: 8 }}>Misure tecniche</div>
                {[
                  { key: "refettorio",  label: "Refettorio/mensa" },
                  { key: "spogliatoio", label: "Spogliatoio" },
                  { key: "baracche",    label: "Servizi e baracche" },
                  { key: "ufficio",     label: "Ufficio in cantiere" },
                  { key: "wcChimico",   label: "WC chimico" },
                ].map(r => {
                  const v = rifiutiCantiere.misure[r.key] || { stato: "", note: "" };
                  return (
                    <div key={r.key} style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 3 }}>{r.label}</div>
                      <div style={{ display: "flex", gap: 10, marginBottom: 4 }}>
                        {[{v:"si",l:"SI"},{v:"no",l:"NO"}].map(o => (
                          <label key={o.v} style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 11, cursor: "pointer" }}>
                            <input type="radio" name={`rm_${r.key}`} checked={v.stato === o.v}
                              onChange={() => aggiornaRifMisure(r.key, "stato", o.v)} />
                            <span>{o.l}</span>
                          </label>
                        ))}
                      </div>
                      <input style={{ width: '100%', padding: '4px 8px', background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 4, color: COLORS.text, fontSize: 11 }}
                        placeholder="Misure / note"
                        value={v.note}
                        onChange={e => aggiornaRifMisure(r.key, "note", e.target.value)} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* === F8.1 NOTE LIBERE === */}
          <div style={{ marginBottom: 16, border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: 12 }}>
            <div style={sezioneTitSt}>📝 Note specifiche del cantiere</div>
            <div style={{ fontSize: 11, color: COLORS.textMuted, fontStyle: 'italic', marginBottom: 8 }}>
              Spazio libero per annotazioni richieste dal committente, prescrizioni speciali, peculiarita' del cantiere ecc. (max 3000 caratteri)
            </div>
            <textarea
              style={{
                width: '100%', minHeight: 100, padding: '8px 10px',
                background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 4,
                color: COLORS.text, fontSize: 13, fontFamily: 'inherit', resize: 'vertical',
              }}
              placeholder="Annotazioni libere per questo cantiere..."
              value={noteCantiere}
              maxLength={3000}
              onChange={e => setNoteCantiere(e.target.value)}
            />
            <div style={{ fontSize: 10, color: COLORS.textDim, textAlign: 'right', marginTop: 3 }}>
              {noteCantiere.length}/3000
            </div>
          </div>

          {/* === MACCHINE IN CANTIERE (Step B) === */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={sezioneTitSt}>
                🛠️ Macchine e attrezzature in cantiere ({macchineCantiere.size}/{macchineDisponibili.length})
              </div>
              <button onClick={() => setMacchineCantiere(new Set())} style={{
                background: 'none', border: 'none', color: COLORS.textMuted,
                fontSize: 10, cursor: 'pointer', textTransform: 'uppercase',
              }}>Deseleziona tutte</button>
            </div>

            {loadingMacchine ? (
              <div style={{ fontSize: 12, color: COLORS.textMuted, padding: 10 }}>Caricamento macchine...</div>
            ) : macchineDisponibili.length === 0 ? (
              <div style={{ fontSize: 12, color: COLORS.textMuted, padding: 10, fontStyle: 'italic' }}>
                Nessuna macchina configurata. Aggiungi le tue attrezzature in Impostazioni → 🛠️ Macchine.
              </div>
            ) : (
              <div style={{ maxHeight: 220, overflowY: 'auto', border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: 8 }}>
                {macchineDisponibili.map(m => {
                  const sel = macchineCantiere.has(m.id);
                  return (
                    <div key={m.id} onClick={() => toggleMacchina(m.id)} style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px',
                      cursor: 'pointer', borderRadius: 4,
                      background: sel ? COLORS.accentSoft : 'transparent',
                    }}>
                      <div style={{
                        width: 16, height: 16, borderRadius: 3, flexShrink: 0,
                        border: `2px solid ${sel ? COLORS.accent : COLORS.border}`,
                        background: sel ? COLORS.accent : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 10, color: '#1a1d27', fontWeight: 700,
                      }}>{sel ? '✓' : ''}</div>
                      <div style={{ fontSize: 12, color: COLORS.text, flex: 1 }}>
                        🔧 {m.nome}
                      </div>
                      <div style={{ display: 'flex', gap: 6, fontSize: 11, color: COLORS.textMuted }}>
                        {m.libretto && <span title="Libretto">📘</span>}
                        {m.collaudo && <span title="Collaudo">✅</span>}
                        {m.verifiche && <span title="Verifiche periodiche">🔄</span>}
                        {m.usoComune && <span title="Uso comune">👥</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* === DOCUMENTI DA ALLEGARE (NEW) === */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={sezioneTitSt}>
                📎 Documenti da allegare ({documentiSelezionati.size})
              </div>
              <button onClick={() => setDocumentiSelezionati(new Set())} style={{
                background: 'none', border: 'none', color: COLORS.textMuted,
                fontSize: 10, cursor: 'pointer', textTransform: 'uppercase',
              }}>Deseleziona tutti</button>
            </div>

            {loadingDoc ? (
              <div style={{ fontSize: 12, color: COLORS.textMuted, padding: 10 }}>Caricamento documenti...</div>
            ) : (
              <div style={{ maxHeight: 260, overflowY: 'auto', border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: 8 }}>

                {/* DOCUMENTI AZIENDA */}
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 10, color: COLORS.accent, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>
                    Azienda ({totDocAzienda})
                  </div>
                  {appiattisciDocumenti(documentiDisponibili.azienda || []).map(f => {
                    const sel = documentiSelezionati.has(f.path);
                    return (
                      <div key={f.path} onClick={() => toggleDocumento(f.path)} style={{
                        display: 'flex', alignItems: 'center', gap: 6, padding: '3px 6px',
                        cursor: 'pointer', borderRadius: 4,
                        background: sel ? COLORS.accentSoft : 'transparent',
                      }}>
                        <div style={{
                          width: 14, height: 14, borderRadius: 3, flexShrink: 0,
                          border: `2px solid ${sel ? COLORS.accent : COLORS.border}`,
                          background: sel ? COLORS.accent : 'transparent',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 9, color: '#1a1d27', fontWeight: 700,
                        }}>{sel ? '✓' : ''}</div>
                        <div style={{ fontSize: 11, color: COLORS.text, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {f.nome}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* DOCUMENTI LAVORATORI (espandibili) */}
                <div>
                  <div style={{ fontSize: 10, color: COLORS.accent, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>
                    Personale ({totDocLav})
                  </div>
                  {Object.entries(documentiDisponibili.lavoratori || {}).map(([nomeLav, files]) => {
                    const flatFiles = appiattisciDocumenti(files);
                    const espanso = lavoratoriEspansi.has(nomeLav);
                    const selezTrLav = flatFiles.filter(f => documentiSelezionati.has(f.path)).length;
                    return (
                      <div key={nomeLav} style={{ marginBottom: 4 }}>
                        <div onClick={() => toggleLavoratoreEspanso(nomeLav)} style={{
                          display: 'flex', alignItems: 'center', gap: 6, padding: '4px 6px',
                          cursor: 'pointer', borderRadius: 4,
                          background: COLORS.bg,
                        }}>
                          <div style={{ fontSize: 12, color: COLORS.textMuted, width: 12 }}>
                            {espanso ? '▾' : '▸'}
                          </div>
                          <div style={{ fontSize: 11, color: COLORS.text, fontWeight: 600, flex: 1 }}>
                            {nomeLav}
                          </div>
                          <div style={{ fontSize: 10, color: COLORS.textDim }}>
                            {selezTrLav}/{flatFiles.length}
                          </div>
                        </div>
                        {espanso && flatFiles.map(f => {
                          const sel = documentiSelezionati.has(f.path);
                          return (
                            <div key={f.path} onClick={() => toggleDocumento(f.path)} style={{
                              display: 'flex', alignItems: 'center', gap: 6, padding: '3px 6px 3px 24px',
                              cursor: 'pointer', borderRadius: 4,
                              background: sel ? COLORS.accentSoft : 'transparent',
                            }}>
                              <div style={{
                                width: 14, height: 14, borderRadius: 3, flexShrink: 0,
                                border: `2px solid ${sel ? COLORS.accent : COLORS.border}`,
                                background: sel ? COLORS.accent : 'transparent',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 9, color: '#1a1d27', fontWeight: 700,
                              }}>{sel ? '✓' : ''}</div>
                              <div style={{ fontSize: 11, color: COLORS.text, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {f.nome}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>

              </div>
            )}
          </div>

        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: 8,
          paddingTop: 14, marginTop: 12, borderTop: `1px solid ${COLORS.border}`,
        }}>
          <button onClick={onClose} disabled={generando} style={{
            background: 'transparent', border: `1px solid ${COLORS.border}`,
            color: COLORS.text, padding: '8px 18px', borderRadius: 8,
            fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
          }}>Annulla</button>
          <button onClick={generaPOS} disabled={generando || lavoratoriSelezionati.size === 0} style={{
            background: generando ? COLORS.textDim : COLORS.accent,
            border: 'none', color: '#1a1d27', padding: '8px 18px',
            borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer',
            fontFamily: 'inherit',
            opacity: (generando || lavoratoriSelezionati.size === 0) ? 0.6 : 1,
          }}>
            {generando ? 'Generazione...' : '📋 Genera POS'}
          </button>
        </div>
      </div>
    </div>
  );
}
